import React, { useState } from 'react';
import { Search, TrendingUp, ShieldCheck, Banknote, Building2, Package, ClipboardList, BarChart3, CheckSquare, FileText } from 'lucide-react';

interface App {
  id: string;
  label: string;
  icon: React.ReactNode;
  description: string;
  category: string;
}

interface ApplicationSearchBarProps {
  onSelectApp: (appId: string) => void;
  permissions?: Record<string, any>;
  isAdmin?: boolean;
}

export function ApplicationSearchBar({ onSelectApp, permissions = {}, isAdmin = false }: ApplicationSearchBarProps) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const apps: App[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <BarChart3 className="w-4 h-4" />,
      description: 'Main dashboard with overview',
      category: 'Core',
    },
    {
      id: 'products',
      label: 'Product Reminders',
      icon: <Package className="w-4 h-4" />,
      description: 'Manage product reminders',
      category: 'Reminders',
    },
    {
      id: 'tasks',
      label: 'Task Reminders',
      icon: <ClipboardList className="w-4 h-4" />,
      description: 'Create and manage task reminders',
      category: 'Reminders',
    },
    {
      id: 'emd',
      label: 'EMD / ePBG',
      icon: <Banknote className="w-4 h-4" />,
      description: 'Earnest Money Deposit management',
      category: 'Finance',
    },
    {
      id: 'sepl',
      label: 'SEPL Funnel',
      icon: <TrendingUp className="w-4 h-4" />,
      description: 'Sales opportunity pipeline',
      category: 'Sales',
    },
    {
      id: 'insurance',
      label: 'Insurance Portal',
      icon: <ShieldCheck className="w-4 h-4" />,
      description: 'Insurance management system',
      category: 'Systems',
    },
    {
      id: 'billing-portal',
      label: 'Billing Portal',
      icon: <Banknote className="w-4 h-4" />,
      description: 'Project and billing management',
      category: 'Billing',
    },
    {
      id: 'hr-recruitement',
      label: 'Enroll Applicant',
      icon: <Building2 className="w-4 h-4" />,
      description: 'Applicant enrollment and candidate lifecycle tracking',
      category: 'HR',
    },
    {
      id: 'tasktracker',
      label: 'Activity Management Portal',
      icon: <CheckSquare className="w-4 h-4" />,
      description: 'Centralized activity management with scheduling and tracking',
      category: 'Productivity',
    },
    {
      id: 'cost-sheet',
      label: 'Cost Sheet Portal',
      icon: <FileText className="w-4 h-4" />,
      description: 'Create and approve cost sheets with versioning and workflow',
      category: 'Finance',
    },
    {
      id: 'letters-dashboard',
      label: 'Letter Generation',
      icon: <FileText className="w-4 h-4" />,
      description: 'Create, draft, manage, and export official letters with audit history',
      category: 'Documents',
    },
  ];

  const permissionMap: Record<string, string> = {
    products: 'can_view_products',
    tasks: 'can_view_tasks',
    emd: 'can_view_emd',
    sepl: 'can_view_sepl',
    insurance: 'can_view_insurance',
    'billing-portal': 'can_view_billing_portal',
    'hr-recruitement': 'can_view_hr_recruitment',
    tasktracker: 'can_view_tasktracker',
    'cost-sheet': 'can_view_cost_sheet',
    'letters-dashboard': 'can_view_letters',
  };

  const hasAccess = (appId: string): boolean => {
    if (appId === 'dashboard') return true; // Dashboard always accessible
    const permKey = permissionMap[appId];
    if (!permKey) return true; // If no permission key defined, show it
    
    if (!permissions || Object.keys(permissions).length === 0) return false;
    
    const hasPermission = (permissions as any)[permKey] === true || (permissions as any)[permKey] === 1;
    return isAdmin || hasPermission;
  };

  const accessibleApps = apps.filter(app => hasAccess(app.id));

  const filteredApps = accessibleApps.filter(
    (app) =>
      app.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectApp = (appId: string) => {
    onSelectApp(appId);
    setSearchOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="relative">
      {/* Search Button */}
      <button
        onClick={() => setSearchOpen(!searchOpen)}
        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white border border-gray-300 hover:border-blue-500 hover:shadow-md transition text-gray-700 font-semibold"
      >
        <Search className="w-5 h-5" />
        <span className="hidden md:inline">Search Applications</span>
      </button>

      {/* Search Modal */}
      {searchOpen && (
        <div className="fixed inset-0 z-40" onClick={() => setSearchOpen(false)}>
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black bg-opacity-50" />

          {/* Search Panel */}
          <div className="absolute top-0 left-0 right-0 z-50 p-4 max-w-2xl mx-auto pt-20">
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
              {/* Search Input */}
              <div className="p-4 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search applications..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Apps List */}
              <div className="max-h-96 overflow-y-auto">
                {filteredApps.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <p className="text-lg">No applications found</p>
                    <p className="text-sm mt-2">Try searching with different keywords</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredApps.map((app) => (
                      <button
                        key={app.id}
                        onClick={() => handleSelectApp(app.id)}
                        className="w-full flex items-start gap-3 p-4 hover:bg-blue-50 transition text-left"
                      >
                        <div className="flex-shrink-0 p-2 bg-blue-100 rounded-lg text-blue-600 mt-1">
                          {app.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900">{app.label}</p>
                          <p className="text-sm text-gray-600">{app.description}</p>
                          <p className="text-xs text-gray-500 mt-1 uppercase tracking-wide">
                            {app.category}
                          </p>
                        </div>
                        <div className="text-gray-400">→</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
                <p className="text-xs text-gray-600">
                  Press <kbd className="px-2 py-1 bg-gray-200 rounded text-gray-800 font-mono">Esc</kbd> to close
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Close on Escape */}
      {searchOpen && (
        <div
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setSearchOpen(false);
              setSearchTerm('');
            }
          }}
          tabIndex={-1}
        />
      )}
    </div>
  );
}
